import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

// ---------- singleton settings cache ----------
// One promise, shared across every <SEO> mount. Keeps us from hitting the
// backend on every route change; the resolved value is broadcast to
// subscribers so a settings change (after a PUT) can re-render all pages.
let _seoPromise = null;
let _seoData = null;
const _subs = new Set();

export function loadSeoSettings() {
  if (_seoPromise) return _seoPromise;
  _seoPromise = api.get("/seo/settings")
    .then((r) => {
      _seoData = r.data || {};
      _subs.forEach((fn) => fn(_seoData));
      return _seoData;
    })
    .catch(() => {
      _seoData = {};
      return _seoData;
    });
  return _seoPromise;
}

export function subscribeSeo(fn) {
  _subs.add(fn);
  if (_seoData) fn(_seoData);
  return () => _subs.delete(fn);
}

export function invalidateSeoCache() {
  _seoPromise = null;
  _seoData = null;
  loadSeoSettings().then((d) => _subs.forEach((fn) => fn(d)));
}

// ---------- route registration (auto-discovery for the SEO admin) ----------
// Dedupe per session per pageKey so we don't spam the register endpoint on
// every re-render. Fire-and-forget: registration must NEVER block or break
// the page render, so we swallow errors silently.
const _registeredKeys = new Set();
export function registerSeoPage({ key, path, default_title, default_description }) {
  if (!key || _registeredKeys.has(key)) return;
  _registeredKeys.add(key);
  api.post("/seo/pages/register", {
    key,
    path: path || "/",
    default_title: default_title || undefined,
    default_description: default_description || undefined,
  }).catch(() => {
    _registeredKeys.delete(key);
  });
}

// ---------- GA4 injector (needs to run exactly once) ----------
let _ga4Injected = null;
function injectGa4(id) {
  if (!id || _ga4Injected === id || typeof document === "undefined") return;
  _ga4Injected = id;
  const s1 = document.createElement("script");
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  s1.setAttribute("data-ga4", "true");
  document.head.appendChild(s1);
  const s2 = document.createElement("script");
  s2.setAttribute("data-ga4", "true");
  s2.text = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${id}');`;
  document.head.appendChild(s2);
}

/**
 * SEO — declarative <head> for React pages.
 *
 * Resolution order (highest wins):
 *   1. Admin override for this pageKey (settings.pages[key])
 *   2. Site defaults from the backend (settings.default_*)
 *   3. Local component props (dev-time fallback)
 *   4. Hardcoded shell defaults
 *
 * Rendering: uses react-helmet-async so client-side navigation swaps the
 * title/description synchronously (no flash, no manual DOM manipulation),
 * and the same output can be extracted to a stream if we ever add SSR.
 */
export default function SEO({ title, description, canonical, ogImage, pageKey }) {
  const [settings, setSettings] = useState(_seoData || {});

  useEffect(() => {
    loadSeoSettings().then(setSettings);
    const unsub = subscribeSeo(setSettings);
    return unsub;
  }, []);

  // Auto-register this route with the SEO admin registry so any newly-added
  // page shows up in /app/seo without a code change. Deduped per-session.
  useEffect(() => {
    if (pageKey && typeof window !== "undefined") {
      registerSeoPage({
        key: pageKey,
        path: window.location.pathname,
        default_title: title,
        default_description: description,
      });
    }
  }, [pageKey, title, description]);

  // GA4 is a side-effect script, not a <Helmet> tag (gtag config needs to
  // run exactly once regardless of Helmet re-renders).
  useEffect(() => {
    if (settings.ga4_id) injectGa4(settings.ga4_id);
  }, [settings.ga4_id]);

  const override = (settings.pages && pageKey && settings.pages[pageKey]) || {};
  // Resolution order: admin override > page's own prop > site default > null.
  // Admin overrides always win. If the admin hasn't customized this route,
  // the page's own title/description keeps the page distinct in search
  // results and browser tabs; the site default kicks in only when both
  // are absent (e.g. a new route wired up before its author set props).
  const finalTitle = override.title || title || settings.default_title;
  const finalDesc = override.description || description || settings.default_description;
  const finalCanonical = override.canonical || canonical;
  const finalOgImage = settings.default_og_image || ogImage;
  const siteName = settings.site_name || "SerpNex";
  const twitterHandle = settings.twitter_handle;
  const gsc = settings.gsc_verification;
  const twitterCard = finalOgImage ? "summary_large_image" : "summary";

  return (
    <Helmet>
      {finalTitle && <title>{finalTitle}</title>}
      {finalDesc && <meta name="description" content={finalDesc} />}
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}
      <meta property="og:site_name" content={siteName} />
      {finalTitle && <meta property="og:title" content={finalTitle} />}
      {finalDesc && <meta property="og:description" content={finalDesc} />}
      {finalOgImage && <meta property="og:image" content={finalOgImage} />}
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content={twitterCard} />
      {finalTitle && <meta name="twitter:title" content={finalTitle} />}
      {finalDesc && <meta name="twitter:description" content={finalDesc} />}
      {finalOgImage && <meta name="twitter:image" content={finalOgImage} />}
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {gsc && <meta name="google-site-verification" content={gsc} />}
    </Helmet>
  );
}
